export * from "./types";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import type { ColorKeys, DatepickerType, DateType, Period } from "./types";

import Calendar from "./components/Calendar";
import Footer from "./components/Footer";
import Arrow from "./components/icons/Arrow";
import Input from "./components/Input";
import Shortcuts from "./components/Shortcuts";
import VerticalDash from "./components/VerticalDash";
import {
  COLORS,
  DATE_FORMAT,
  DEFAULT_COLOR,
  DEFAULT_DATE_LOOKING,
  DEFAULT_SEPARATOR,
  LANGUAGE,
  START_WEEK,
} from "./constants";
import DatepickerContext from "./contexts/DatepickerContext";
import { updateLimitDate } from "./helpers";
import useOnClickOutside from "./hooks";
import {
  dateFormat,
  dateIsAfter,
  dateIsSameOrAfter,
  dateIsSameOrBefore,
  dateIsValid,
  dateUpdateMonth,
  dateUpdateYear,
  firstDayOfMonth,
  nextMonthBy,
  previousMonthBy,
} from "./libs/date";

const Datepicker = (props: DatepickerType) => {
  // Props
  const {
    asSingle = false,

    classNames = undefined,
    configs = undefined,
    containerClassName = null,

    dateLooking = DEFAULT_DATE_LOOKING,
    disabledDates = null,
    disabled = false,
    displayFormat = DATE_FORMAT,

    i18n = LANGUAGE,
    inputClassName = null,
    inputId,
    inputName,

    minDate: minDateProps = undefined,
    maxDate: maxDateProps = undefined,
    limitDays = undefined,

    onChange,

    placeholder = null,
    popupClassName = null,
    popoverDirection = undefined,
    primaryColor = DEFAULT_COLOR,

    separator = DEFAULT_SEPARATOR,
    showFooter = false,
    showShortcuts = false,
    startFrom = null,
    startWeekOn = START_WEEK,

    readOnly = false,
    required = false,

    toggleClassName = null,
    toggleIcon = undefined,
    trigger = undefined,

    useRange = true,
    positionStrategy = "absolute",
    value = null,
    onOpenChange = undefined,
  } = props;
  const renderInPortal = positionStrategy === "fixed";

  // Refs
  const containerRef = useRef<HTMLDivElement | null>(null);
  const calendarContainerRef = useRef<HTMLDivElement | null>(null);
  const arrowRef = useRef<HTMLDivElement | null>(null);
  const footerContainerRef = useRef<HTMLDivElement | null>(null);

  // States
  const [firstDate, setFirstDate] = useState<Date>(
    startFrom && dateIsValid(startFrom) ? startFrom : new Date(),
  );
  const [secondDate, setSecondDate] = useState<Date>(nextMonthBy(firstDate));
  const [period, setPeriod] = useState<Period>({
    start: null,
    end: null,
  });
  const [dayHover, setDayHover] = useState<DateType>(null);
  const [inputText, setInputText] = useState<string>("");
  const [input, setInput] = useState<HTMLInputElement | null>(null);

  const [minDate, setMinDate] = useState<DateType | undefined>(minDateProps);
  const [maxDate, setMaxDate] = useState<DateType | undefined>(maxDateProps);
  const [isOpen, setIsOpen] = useState(false);

  const previousIsOpenRef = useRef(isOpen);
  useEffect(() => {
    if (previousIsOpenRef.current === isOpen) return;
    previousIsOpenRef.current = isOpen;
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);

  const [portalPosition, setPortalPosition] = useState<{
    top: number;
    right: number;
    contentMaxHeight: number | null;
  } | null>(null);

  // Custom Hooks use
  useOnClickOutside(
    containerRef.current,
    () => {
      const container = containerRef.current;
      if (container) {
        cancelDatepicker();
      }
    },
    // The popup itself, in case positionStrategy="fixed" moved it outside
    // containerRef's subtree — without this, any click on the calendar
    // (including selecting a date) would register as "outside" and close it
    // first.
    calendarContainerRef.current,
  );

  // Functions
  const resetLimitDate = useCallback(() => {
    setMinDate(minDateProps);
    setMaxDate(maxDateProps);
  }, [minDateProps, maxDateProps]);

  const hideDatepicker = useCallback(() => {
    const div = calendarContainerRef.current;
    const arrow = arrowRef.current;
    if (arrow && div && div.classList.contains("block")) {
      div.classList.remove("block");
      div.classList.remove("translate-y-0");
      div.classList.remove("opacity-1");
      div.classList.add("translate-y-4");
      div.classList.add("opacity-0");
      setTimeout(() => {
        div.classList.remove("bottom-full");
        div.classList.add("hidden");
        div.classList.add("mb-2.5");
        div.classList.add("mt-2.5");
        arrow.classList.remove("-bottom-2");
        arrow.classList.remove("border-r");
        arrow.classList.remove("border-b");
        arrow.classList.add("border-l");
        arrow.classList.add("border-t");
      }, 300);
    }
    setIsOpen(false);
  }, []);

  // Discards an unapplied draft selection back to the last applied `value`.
  // Called only on close paths that bypass Apply (outside click, Escape,
  // Cancel) — Apply's own close relies on the existing value->period sync
  // effect below instead, since `value` here would still be one render
  // behind the value Apply just committed.
  const discardUnappliedSelection = useCallback(() => {
    // Hover preview and any limitDays-narrowed bounds are draft-only artifacts
    // of the in-progress selection — clear them regardless of what `value`
    // reverts to below.
    setDayHover(null);
    resetLimitDate();

    if (
      value &&
      value.startDate &&
      value.endDate &&
      dateIsSameOrBefore(value.startDate, value.endDate, "date")
    ) {
      setPeriod({ start: value.startDate, end: value.endDate });
      setInputText(
        `${dateFormat(value.startDate, displayFormat, i18n)}${
          asSingle
            ? ""
            : ` ${separator} ${dateFormat(value.endDate, displayFormat, i18n)}`
        }`,
      );
    } else {
      setPeriod({ start: null, end: null });
      setInputText("");
    }
  }, [value, displayFormat, i18n, asSingle, separator, resetLimitDate]);

  // Close paths that bypass Apply (outside click, Escape, Cancel, re-clicking
  // the trigger while open) go through this instead of hideDatepicker alone.
  const cancelDatepicker = useCallback(() => {
    discardUnappliedSelection();
    hideDatepicker();
  }, [discardUnappliedSelection, hideDatepicker]);

  const showDatepicker = useCallback(() => {
    setIsOpen(true);
  }, []);

  /* Start First */
  const firstGotoDate = useCallback(
    (date: Date) => {
      if (dateIsSameOrAfter(date, secondDate, "date")) {
        setSecondDate(nextMonthBy(date));
      }
      setFirstDate(date);
    },
    [secondDate],
  );

  const previousMonthFirst = useCallback(() => {
    setFirstDate(previousMonthBy(firstDate));
  }, [firstDate]);

  const nextMonthFirst = useCallback(() => {
    firstGotoDate(nextMonthBy(firstDate));
  }, [firstDate, firstGotoDate]);

  const changeFirstMonth = useCallback(
    (month: number) => {
      firstGotoDate(dateUpdateMonth(firstDate, month - 1));
    },
    [firstDate, firstGotoDate],
  );

  const changeFirstYear = useCallback(
    (year: number) => {
      firstGotoDate(dateUpdateYear(firstDate, year));
    },
    [firstDate, firstGotoDate],
  );
  /* End First */

  /* Start Second */
  const secondGotoDate = useCallback(
    (date: Date) => {
      dateIsSameOrBefore(date, firstDate, "date");
      if (dateIsSameOrBefore(date, firstDate, "date")) {
        setFirstDate(previousMonthBy(date));
      }
      setSecondDate(date);
    },
    [firstDate],
  );

  const previousMonthSecond = useCallback(() => {
    secondGotoDate(previousMonthBy(secondDate));
  }, [secondDate, secondGotoDate]);

  const nextMonthSecond = useCallback(() => {
    setSecondDate(nextMonthBy(secondDate));
  }, [secondDate]);

  const changeSecondMonth = useCallback(
    (month: number) => {
      secondGotoDate(dateUpdateMonth(secondDate, month - 1));
    },
    [secondDate, secondGotoDate],
  );

  const changeSecondYear = useCallback(
    (year: number) => {
      secondGotoDate(dateUpdateYear(secondDate, year));
    },
    [secondDate, secondGotoDate],
  );
  /* End Second */

  // UseEffects & UseLayoutEffect
  useEffect(() => {
    const container = containerRef.current;
    const calendarContainer = calendarContainerRef.current;
    const arrow = arrowRef.current;

    if (container && calendarContainer && arrow) {
      const detail = container.getBoundingClientRect();
      const screenCenter = window.innerWidth / 2;
      const containerCenter = (detail.right - detail.x) / 2 + detail.x;

      if (containerCenter > screenCenter) {
        arrow.classList.add("right-0");
        arrow.classList.add("mr-3.5");
        calendarContainer.classList.add("right-0");
      }
    }
  }, []);

  useLayoutEffect(() => {
    if (!renderInPortal || !isOpen) return;

    // Tracks the largest measured height this session so a later capped
    // (smaller) render can't be misread as "it fits now" and oscillate.
    let naturalPopupHeight = 0;

    const updatePosition = () => {
      const containerEl = containerRef.current;
      if (!containerEl) return;
      const gap = 4;
      const minMargin = 8;
      const rect = containerEl.getBoundingClientRect();
      const popupRect = calendarContainerRef.current?.getBoundingClientRect();
      const popupWidth = popupRect?.width ?? 0;
      const footerHeight =
        footerContainerRef.current?.getBoundingClientRect().height ?? 0;
      naturalPopupHeight = Math.max(naturalPopupHeight, popupRect?.height ?? 0);
      const popupHeight = naturalPopupHeight;

      const fitsBelow =
        popupHeight > 0 &&
        rect.bottom + gap + popupHeight + minMargin <= window.innerHeight;
      const topAbove = rect.top - gap - popupHeight;
      const fitsAbove = popupHeight > 0 && topAbove >= minMargin;
      let top: number;
      if (fitsBelow) {
        top = rect.bottom + gap;
      } else if (fitsAbove) {
        top = topAbove;
      } else {
        top = Math.max(
          minMargin,
          Math.min(
            rect.bottom + gap,
            window.innerHeight - popupHeight - minMargin,
          ),
        );
      }

      let right = Math.max(0, window.innerWidth - rect.right);
      if (popupWidth > 0) {
        right = Math.min(
          right,
          Math.max(minMargin, window.innerWidth - popupWidth - minMargin),
        );
      }

      const viewportBudget = window.innerHeight - 2 * minMargin;
      const contentMaxHeight =
        popupHeight > viewportBudget
          ? Math.max(viewportBudget - footerHeight, 100)
          : null;

      setPortalPosition((prev) =>
        prev &&
        prev.top === top &&
        prev.right === right &&
        prev.contentMaxHeight === contentMaxHeight
          ? prev
          : { top, right, contentMaxHeight },
      );
    };

    updatePosition();
    const raf = requestAnimationFrame(updatePosition);

    let pendingRaf: number | null = null;
    const scheduleUpdate = () => {
      if (pendingRaf !== null) return;
      pendingRaf = requestAnimationFrame(() => {
        pendingRaf = null;
        updatePosition();
      });
    };

    // capture: true — a scroll event on a nested scrollable ancestor (the
    // drawer's own body) doesn't bubble to window; a capturing listener
    // still catches it on the way down.
    window.addEventListener("scroll", scheduleUpdate, true);
    window.addEventListener("resize", scheduleUpdate);

    const popupEl = calendarContainerRef.current;
    const resizeObserver = popupEl ? new ResizeObserver(scheduleUpdate) : null;
    if (popupEl) resizeObserver?.observe(popupEl);

    return () => {
      cancelAnimationFrame(raf);
      if (pendingRaf !== null) cancelAnimationFrame(pendingRaf);
      window.removeEventListener("scroll", scheduleUpdate, true);
      window.removeEventListener("resize", scheduleUpdate);
      resizeObserver?.disconnect();
    };
  }, [renderInPortal, isOpen]);

  useEffect(() => {
    if (value && value.startDate && value.endDate) {
      if (dateIsSameOrBefore(value.startDate, value.endDate, "date")) {
        setPeriod({
          start: value.startDate,
          end: value.endDate,
        });

        setInputText(
          `${dateFormat(value.startDate, displayFormat, i18n)}${
            asSingle
              ? ""
              : ` ${separator} ${dateFormat(value.endDate, displayFormat, i18n)}`
          }`,
        );
      }
    }

    if (value && value.startDate === null && value.endDate === null) {
      setPeriod({
        start: null,
        end: null,
      });

      setInputText("");
    }
  }, [asSingle, value, displayFormat, separator, i18n]);

  useEffect(() => {
    if (startFrom && dateIsValid(startFrom)) {
      const startDate = value?.startDate;
      const endDate = value?.endDate;

      if (startDate && dateIsValid(startDate)) {
        setFirstDate(startDate);
        if (!asSingle) {
          if (
            endDate &&
            dateIsValid(endDate) &&
            dateIsAfter(firstDayOfMonth(endDate), startDate, "date")
          ) {
            setSecondDate(endDate);
          } else {
            setSecondDate(nextMonthBy(startDate));
          }
        }
      } else {
        setFirstDate(startFrom);
        setSecondDate(nextMonthBy(startFrom));
      }
    } else {
      // When no startFrom, show previous month on left, current month on right
      const today = new Date();
      setFirstDate(value?.startDate || previousMonthBy(today));
      setSecondDate(value?.endDate || today);
    }
  }, [asSingle, startFrom, value]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      const container = calendarContainerRef.current;

      if (
        !container ||
        !container.classList.contains("block") ||
        event.key !== "Escape"
      ) {
        return;
      }

      // Capture phase + stopPropagation so this only closes the calendar:
      // it runs before any bubble-phase listener beneath it (e.g. a
      // Drawer's own Escape handler) gets a chance to also fire.
      event.stopPropagation();
      cancelDatepicker();
    };

    document.addEventListener("keydown", handleEscapeKey, true);

    return () => {
      document.removeEventListener("keydown", handleEscapeKey, true);
    };
  }, [cancelDatepicker]);

  // Variables
  const safePrimaryColor = useMemo(() => {
    if (COLORS.includes(primaryColor)) {
      return primaryColor as ColorKeys;
    }
    return DEFAULT_COLOR;
  }, [primaryColor]);

  const onClickDayChange = (firstDate: DateType, secondDate?: DateType) => {
    if (!limitDays) return;
    const { minDate, maxDate } = updateLimitDate(
      limitDays,
      firstDate,
      secondDate,
      minDateProps,
      maxDateProps,
    );

    setMinDate(minDate);
    setMaxDate(maxDate);
  };

  const contextValues = useMemo(() => {
    if (minDate && !dateIsValid(minDate)) {
      console.error(`minDate (${minDate}) is invalid date`);
    }

    if (maxDate && !dateIsValid(maxDate)) {
      console.error(`minDate (${maxDate}) is invalid date`);
    }

    if (!i18n || i18n.length === 0) {
      console.error(`i18n (${i18n}) is invalid`);
    }

    return {
      arrowContainer: arrowRef,
      asSingle,
      calendarContainer: calendarContainerRef,
      changeDatepickerValue: onChange,
      changeDayHover: (newDay: DateType) => setDayHover(newDay),
      changeInputText: (newText: string) => setInputText(newText),
      changePeriod: (newPeriod: Period) => {
        setPeriod(newPeriod);
        if (newPeriod.start && newPeriod.end) {
          resetLimitDate();
        }
      },
      cancelDatepicker,
      classNames,
      configs,
      containerClassName,
      dateLooking,
      dayHover,
      disabled,
      disabledDates,
      displayFormat,
      hideDatepicker,
      i18n: i18n && i18n.length > 0 ? i18n : LANGUAGE,
      input,
      setInput: (value: HTMLInputElement | null) => setInput(value),
      inputClassName,
      inputId,
      inputName,
      inputText,
      isOpen,
      maxDate,
      minDate,
      onChange,
      period,
      placeholder,
      popoverDirection,
      primaryColor: safePrimaryColor,
      readOnly,
      required,
      separator,
      showDatepicker,
      showFooter,
      startWeekOn: startWeekOn || START_WEEK,
      toggleClassName,
      toggleIcon,
      trigger,
      updateFirstDate: (newDate: Date) => firstGotoDate(newDate),
      value,
    };
  }, [
    minDate,
    maxDate,
    resetLimitDate,
    i18n,
    asSingle,
    onChange,
    cancelDatepicker,
    classNames,
    configs,
    containerClassName,
    dateLooking,
    dayHover,
    disabled,
    disabledDates,
    displayFormat,
    hideDatepicker,
    input,
    inputClassName,
    inputId,
    inputName,
    inputText,
    isOpen,
    period,
    placeholder,
    popoverDirection,
    safePrimaryColor,
    readOnly,
    showDatepicker,
    required,
    separator,
    showFooter,
    startWeekOn,
    toggleClassName,
    toggleIcon,
    trigger,
    value,
    firstGotoDate,
  ]);

  const containerClassNameOverload = useMemo(() => {
    const defaultContainerClassName = "relative w-full text-gray-700";
    return typeof containerClassName === "function"
      ? containerClassName(defaultContainerClassName)
      : typeof containerClassName === "string" && containerClassName !== ""
        ? containerClassName
        : defaultContainerClassName;
  }, [containerClassName]);

  const popupClassNameOverload = useMemo(() => {
    const defaultPopupClassName =
      "transition-all ease-out duration-300 absolute z-10 mt-[1px] text-sm lg:text-xs 2xl:text-sm translate-y-4 opacity-0 hidden";
    return typeof popupClassName === "function"
      ? popupClassName(defaultPopupClassName)
      : typeof popupClassName === "string" && popupClassName !== ""
        ? popupClassName
        : defaultPopupClassName;
  }, [popupClassName]);

  const popup = (
    <div
      className={popupClassNameOverload}
      ref={calendarContainerRef}
      // `zIndex` clears the Drawer-style portal's own z-40 wrapper, which
      // otherwise paints on top of this one regardless of DOM append order
      // (both are positioned root-level children of document.body, so
      // explicit z-index — not append order — decides stacking). Only
      // applied once measured to avoid a flash at the CSS default (top-left
      // of the viewport).
      style={
        renderInPortal && portalPosition
          ? {
              position: "fixed",
              top: portalPosition.top,
              right: portalPosition.right,
              left: "auto",
              zIndex: 60,
            }
          : undefined
      }
    >
      {/* Arrow is a decorative caret positioned `absolute` relative to its
          parent; in portal mode the parent's `top`/`right` is the trigger
          area, so it would land on the trigger. Hide it visually via a
          display:none wrapper — keep it mounted because Input.tsx's show/hide
          effect gates on `arrowRef` being non-null. */}
      {renderInPortal ? (
        <div style={{ display: "none" }}>
          <Arrow ref={arrowRef} />
        </div>
      ) : (
        <Arrow ref={arrowRef} />
      )}

      <div className="mt-2.5 rounded-lg border border-gray-300 bg-white px-1 py-0.5 shadow-sm dark:border-slate-600 dark:bg-slate-800 dark:text-white">
        <div
          className="flex flex-col py-2 lg:flex-row"
          style={
            renderInPortal && portalPosition?.contentMaxHeight
              ? {
                  maxHeight: portalPosition.contentMaxHeight,
                  overflowY: "auto",
                }
              : undefined
          }
        >
          {showShortcuts && <Shortcuts />}

          <div
            className={`flex flex-col items-stretch space-y-4 md:flex-row md:space-x-1.5 md:space-y-0 ${
              showShortcuts ? "md:pl-2" : "md:pl-1"
            } pr-2 lg:pr-1`}
          >
            <Calendar
              date={firstDate}
              onClickPrevious={previousMonthFirst}
              onClickNext={nextMonthFirst}
              changeMonth={changeFirstMonth}
              changeYear={changeFirstYear}
              minDate={minDate}
              maxDate={maxDate}
              onClickDayChange={onClickDayChange}
            />

            {useRange && (
              <>
                <div className="flex items-center">
                  <VerticalDash />
                </div>

                <Calendar
                  date={secondDate}
                  onClickPrevious={previousMonthSecond}
                  onClickNext={nextMonthSecond}
                  changeMonth={changeSecondMonth}
                  changeYear={changeSecondYear}
                  minDate={minDate}
                  maxDate={maxDate}
                  onClickDayChange={onClickDayChange}
                />
              </>
            )}
          </div>
        </div>

        {showFooter && renderInPortal ? (
          <div ref={footerContainerRef}>
            <Footer />
          </div>
        ) : (
          showFooter && <Footer />
        )}
      </div>
    </div>
  );

  return (
    <DatepickerContext.Provider value={contextValues}>
      <div className={containerClassNameOverload} ref={containerRef}>
        <Input />

        {renderInPortal ? createPortal(popup, document.body) : popup}
      </div>
    </DatepickerContext.Provider>
  );
};

export default Datepicker;
